import { useTranslation } from 'react-i18next';
import { formatTime } from '@/lib/format';
import type { ConflictMap } from '@/lib/conflicts';
import { jobCount, type BoardItem } from '@/lib/boardItems';
import { JobCard } from './JobCard';
import type { JobDragBinding } from './dragBinding';

/**
 * Everything nobody is booked for — "so nothing falls through the cracks"
 * (M1-09's AC, close to verbatim).
 *
 * Holds two different kinds of job, and says which is which. A job with a time
 * but no technician is late to be assigned; a job with no time at all has not
 * been scheduled yet and could sit here for a week. Merging them into one
 * undifferentiated pile is how the second kind gets forgotten, which is the
 * exact failure the rail exists to prevent.
 */
export function UnassignedRail({
  jobs,
  timeZone,
  dragBinding,
  conflicts,
}: {
  jobs: BoardItem[];
  timeZone: string;
  dragBinding: JobDragBinding;
  conflicts: ConflictMap;
}) {
  const { t } = useTranslation();

  // Scheduled-but-unassigned first, in time order: those have a deadline today.
  const scheduled = jobs
    .filter((job) => job.scheduledStart !== null)
    .sort((a, b) => (a.scheduledStart ?? '').localeCompare(b.scheduledStart ?? ''));
  const unscheduled = jobs.filter((job) => job.scheduledStart === null);

  return (
    <aside
      // The dashboard's unscheduled tile links to `/dispatch#unassigned-rail`
      // (M2-17); `Dispatch` focuses this once the board has loaded, hence -1.
      id="unassigned-rail"
      tabIndex={-1}
      aria-labelledby="dispatch-rail-heading"
      className="w-full shrink-0 rounded-lg border lg:w-64"
      data-testid="unassigned-rail"
      // Dropping here unassigns. The rail carries no technician and no date,
      // so a job dragged back keeps whatever time it had and simply loses its
      // technician — which is what "put this back in the queue" means.
      data-drop-target=""
    >
      <h2
        id="dispatch-rail-heading"
        className="flex h-12 flex-col justify-center border-b px-3 text-sm font-medium"
      >
        {t('dispatch.unassignedTitle')}
        <span className="text-[0.625rem] font-normal text-[var(--color-muted-foreground)]">
          {/* Jobs, not cards: a repipe with two unbooked days is one job (M2-52). */}
          {t('dispatch.jobCount', { count: jobCount(jobs) })}
        </span>
      </h2>

      {jobs.length === 0 ? (
        <p className="px-3 py-6 text-center text-xs text-[var(--color-muted-foreground)]">
          {t('dispatch.railEmpty')}
        </p>
      ) : (
        <div className="max-h-[32rem] space-y-4 overflow-y-auto p-2">
          {scheduled.length > 0 ? (
            <section aria-labelledby="dispatch-rail-scheduled">
              <h3
                id="dispatch-rail-scheduled"
                className="px-1 pb-1 text-[0.625rem] font-medium tracking-wide text-[var(--color-muted-foreground)] uppercase"
              >
                {t('dispatch.railScheduled')}
              </h3>
              <ul className="space-y-2">
                {scheduled.map((job) => (
                  <li key={job.id}>
                    <p className="sr-only">
                      {job.scheduledStart
                        ? t('dispatch.scheduledFor', {
                            time: formatTime(job.scheduledStart, timeZone),
                          })
                        : null}
                    </p>
                    <JobCard
                      job={job}
                      timeZone={timeZone}
                      isDragging={dragBinding.draggingCardId === job.id}
                      handlers={dragBinding.handlersFor(job.id)}
                      conflicts={conflicts.get(job.id)}
                    />
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {unscheduled.length > 0 ? (
            <section aria-labelledby="dispatch-rail-unscheduled">
              <h3
                id="dispatch-rail-unscheduled"
                className="px-1 pb-1 text-[0.625rem] font-medium tracking-wide text-[var(--color-muted-foreground)] uppercase"
              >
                {t('dispatch.railUnscheduled')}
              </h3>
              <ul className="space-y-2">
                {unscheduled.map((job) => (
                  <li key={job.id}>
                    <JobCard
                      job={job}
                      timeZone={timeZone}
                      compact
                      isDragging={dragBinding.draggingCardId === job.id}
                      handlers={dragBinding.handlersFor(job.id)}
                    />
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </div>
      )}
    </aside>
  );
}
